import { TimelineEventStatus } from '../models/enums/timeline-event-status.enum';
import { TimelineEvent } from '../models/timeline-event.model';

const MIN_COMPLETED_EVENT_DURATION_DAYS = 1;
const MILLISECONDS_PER_HOUR = 60 * 60 * 1000;
const MILLISECONDS_PER_DAY = 24 * MILLISECONDS_PER_HOUR;
const PST_UTC_OFFSET_HOURS = 8;
const PDT_UTC_OFFSET_HOURS = 7;

// The marketplace business day is the Pacific-Time calendar day (midnight PT ≈ 3 AM ET) —
// mirrors MarketplaceBusinessCalendar on the backend. Storage/transport stay UTC.
const MARKETPLACE_TIME_ZONE = 'America/Los_Angeles';

const businessDateFormatterPt = new Intl.DateTimeFormat('en-CA', {
    timeZone: MARKETPLACE_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
});

const hourOfDayFormatterPt = new Intl.DateTimeFormat('en-US', {
    timeZone: MARKETPLACE_TIME_ZONE,
    hour: '2-digit',
    hourCycle: 'h23',
});

const explicitOffsetPattern = /(?:Z|[+-]\d{2}:?\d{2})$/;

const toDate = (value: Date | string): Date => {
    if (typeof value !== 'string') {
        return value;
    }
    const isUtcWithoutSuffix =
        value.includes('T') && !explicitOffsetPattern.test(value);
    return new Date(isUtcWithoutSuffix ? `${value}Z` : value);
};

export const getBusinessDayNumberPt = (instantUtc: Date | string): number => {
    const [year, month, day] = businessDateFormatterPt
        .format(toDate(instantUtc))
        .split('-')
        .map(Number);
    return Math.floor(Date.UTC(year, month - 1, day) / MILLISECONDS_PER_DAY);
};

const getHourOfDayPt = (instantUtc: Date): number =>
    Number(hourOfDayFormatterPt.format(instantUtc));

export const getBusinessDayStartUtc = (businessDayNumberPt: number): Date => {
    const utcMidnightMs = businessDayNumberPt * MILLISECONDS_PER_DAY;
    const pdtCandidateUtc = new Date(
        utcMidnightMs + PDT_UTC_OFFSET_HOURS * MILLISECONDS_PER_HOUR
    );
    const isPdtMidnight =
        getBusinessDayNumberPt(pdtCandidateUtc) === businessDayNumberPt &&
        getHourOfDayPt(pdtCandidateUtc) === 0;
    return isPdtMidnight
        ? pdtCandidateUtc
        : new Date(utcMidnightMs + PST_UTC_OFFSET_HOURS * MILLISECONDS_PER_HOUR);
};

export const getDayOffset = (
    instantUtc: Date | string,
    engagementStartUtc: Date | string
): number => {
    return (
        getBusinessDayNumberPt(instantUtc) -
        getBusinessDayNumberPt(engagementStartUtc)
    );
};

export const getProjectedTransitionDateUtc = (
    engagementStartUtc: Date | string,
    dayOffset: number
): Date => {
    return getBusinessDayStartUtc(
        getBusinessDayNumberPt(engagementStartUtc) + dayOffset
    );
};

// An event's day chain is anchored to its actual start once it has one, and to its planned
// day offset until then — mirrors TimelineEvent.GetEffectiveStartBusinessDatePt on the backend.
const getEventEffectiveStartDayNumberPt = (
    engagementStartUtc: Date | string,
    event: TimelineEvent
): number =>
    event.startedAt
        ? getBusinessDayNumberPt(event.startedAt)
        : getBusinessDayNumberPt(engagementStartUtc) + event.dayStart;

export const getEventStartDateUtc = (
    engagementStartUtc: Date | string,
    event: TimelineEvent
): Date =>
    getBusinessDayStartUtc(
        getEventEffectiveStartDayNumberPt(engagementStartUtc, event)
    );

export const getEventDeadlineDateUtc = (
    engagementStartUtc: Date | string,
    event: TimelineEvent
): Date =>
    getBusinessDayStartUtc(
        getEventEffectiveStartDayNumberPt(engagementStartUtc, event) +
            (event.closingLimitedDurationDays ?? event.currentTempoDurationDays) -
            1
    );

export const addBusinessDaysUtc = (
    instantUtc: Date | string,
    days: number
): Date => {
    const instant = toDate(instantUtc);
    const dayNumberPt = getBusinessDayNumberPt(instant);
    const offsetInDayMs =
        instant.getTime() - getBusinessDayStartUtc(dayNumberPt).getTime();
    const targetDayStartMs = getBusinessDayStartUtc(
        dayNumberPt + days
    ).getTime();
    const targetDayEndMs = getBusinessDayStartUtc(
        dayNumberPt + days + 1
    ).getTime();
    const clampedOffsetMs = Math.min(
        offsetInDayMs,
        targetDayEndMs - targetDayStartMs - 1
    );
    return new Date(targetDayStartMs + clampedOffsetMs);
};

export const formatBusinessDatePt = (instantUtc: Date | string): string =>
    toDate(instantUtc).toLocaleDateString(undefined, {
        timeZone: MARKETPLACE_TIME_ZONE,
    });

const longBusinessDateFormatterPt = new Intl.DateTimeFormat('en-US', {
    timeZone: MARKETPLACE_TIME_ZONE,
    year: 'numeric',
    month: 'long',
    day: 'numeric',
});

export const formatLongBusinessDatePt = (instantUtc: Date | string): string =>
    longBusinessDateFormatterPt.format(toDate(instantUtc));

// For date pickers and viewer-local calendar math: the instant's PT business date,
// materialized as midnight in the viewer's local timezone.
export const getBusinessDateStartLocal = (instantUtc: Date | string): Date => {
    const [year, month, day] = businessDateFormatterPt
        .format(toDate(instantUtc))
        .split('-')
        .map(Number);
    return new Date(year, month - 1, day);
};

export const getDisplayedDuration = (event: TimelineEvent): number => {
    const derivedDuration = event.dayEnd - event.dayStart;
    return event.status === TimelineEventStatus.Completed
        ? Math.max(MIN_COMPLETED_EVENT_DURATION_DAYS, derivedDuration)
        : derivedDuration;
};
